/**
 * Время и даты кабинета.
 *
 * ГЛАВНОЕ ПРАВИЛО: кабинет показывает время САЛОНА, а не устройства. Администратор
 * из Москвы, открывший филиал в Екатеринбурге, должен видеть 16:40 — время, в которое
 * туда придёт клиент, — а не своё. Поэтому зона филиала передаётся явно в каждый вызов,
 * а не берётся из браузера.
 *
 * День журнала — тоже календарный день ФИЛИАЛА: «сегодня» во Владивостоке наступает
 * раньше, чем в Москве, и день, посчитанный по устройству, уедет на сутки.
 */

const TIME_OF_DAY = new Map<string, Intl.DateTimeFormat>()
const DATE_LONG = new Map<string, Intl.DateTimeFormat>()
const ISO_DATE = new Map<string, Intl.DateTimeFormat>()

function cached(
  cache: Map<string, Intl.DateTimeFormat>,
  key: string,
  build: () => Intl.DateTimeFormat,
): Intl.DateTimeFormat {
  const existing = cache.get(key)
  if (existing !== undefined) return existing
  const created = build()
  cache.set(key, created)
  return created
}

/** «13:47». Ровно то, что пишется на полосе состояния и на бейдже линии «сейчас». */
export function formatTimeOfDay(instant: string | Date, timeZone: string): string {
  const format = cached(
    TIME_OF_DAY,
    timeZone,
    () =>
      new Intl.DateTimeFormat("ru-RU", {
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
        timeZone,
      }),
  )
  // Та же защита, что и у подписи дня: Intl бросает на недействительном моменте,
  // и одно поле уносит экран целиком. Прочерк видно, исключение — нет.
  const parsed = typeof instant === "string" ? new Date(instant) : instant
  return Number.isNaN(parsed.getTime()) ? "—" : format.format(parsed)
}

/** Календарная дата без времени: ровно `YYYY-MM-DD` и ничего больше. */
const CALENDAR_DATE = /^\d{4}-\d{2}-\d{2}$/

/**
 * «ср, 16 сентября» — подпись дня.
 *
 * ПРИНИМАЕТ ДВА РАЗНЫХ ВИДА ВРЕМЕНИ, и это не удобство, а требование вызывающих:
 * день журнала приходит календарной датой («2026-09-16»), а `createdAt` карточки
 * клиента, отметка согласия и срок ограничения — моментом («2026-09-16T01:20:33Z»).
 *
 * Календарная дата ставится на ПОЛДЕНЬ UTC, а не на полночь: полночь в Москве — это
 * предыдущий день в UTC, и подпись уезжала бы на сутки у половины страны. Момент же
 * разбирается как есть — прибавлять к нему «T12:00:00Z» нельзя.
 *
 * ИМЕННО ЭТИМ ПАДАЛА КАРТОЧКА КЛИЕНТА. Раньше суффикс добавлялся ЛЮБОЙ строке,
 * и момент превращался в «2026-09-16T01:20:33.000ZT12:00:00Z» → Invalid Date →
 * Intl бросает «Invalid time value». Экран, куда администратор идёт разбираться
 * в споре с клиентом, переставал открываться целиком.
 *
 * Неразобранное значение НЕ роняет экран: возвращается «—». Одно кривое поле в одном
 * согласии не должно уносить всю карточку — а пустая дата видна и без исключения.
 */
export function formatDateLong(date: string | Date, timeZone: string): string {
  const format = cached(
    DATE_LONG,
    timeZone,
    () =>
      new Intl.DateTimeFormat("ru-RU", {
        weekday: "short",
        day: "numeric",
        month: "long",
        timeZone,
      }),
  )
  const parsed =
    typeof date === "string"
      ? new Date(CALENDAR_DATE.test(date) ? `${date}T12:00:00Z` : date)
      : date
  return Number.isNaN(parsed.getTime()) ? "—" : format.format(parsed)
}

/**
 * «12 августа» — день и месяц без дня недели и года.
 *
 * Отдельно от formatDateLong, потому что читается в СТРОКУ с другим текстом: «7 визитов
 * · 0 неявок · последний 12 августа» (5-А3) и «Записей на 16 сентября нет» (5-А1).
 * День недели там лишний — он уже стоит в шапке, — а год в пределах журнала не меняется.
 */
const DAY_MONTH = new Map<string, Intl.DateTimeFormat>()

export function formatDayMonth(date: string | Date, timeZone: string): string {
  const format = cached(
    DAY_MONTH,
    timeZone,
    () => new Intl.DateTimeFormat("ru-RU", { day: "numeric", month: "long", timeZone }),
  )
  const parsed =
    typeof date === "string"
      ? new Date(CALENDAR_DATE.test(date) ? `${date}T12:00:00Z` : date)
      : date
  return Number.isNaN(parsed.getTime()) ? "—" : format.format(parsed)
}

/**
 * Календарная дата филиала прямо сейчас, в виде YYYY-MM-DD.
 *
 * Локаль en-CA выбрана не для красоты: она единственная из распространённых даёт
 * ISO-порядок «год-месяц-день», и дату не приходится собирать из частей руками.
 */
export function todayInZone(timeZone: string, now: Date = new Date()): string {
  const format = cached(
    ISO_DATE,
    timeZone,
    () =>
      new Intl.DateTimeFormat("en-CA", {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        timeZone,
      }),
  )
  return format.format(now)
}

/** Сдвиг календарной даты на N дней. Работает со строкой, чтобы не тащить зону в расчёт. */
export function shiftDate(date: string, days: number): string {
  const parsed = new Date(`${date}T12:00:00Z`)
  parsed.setUTCDate(parsed.getUTCDate() + days)
  return parsed.toISOString().slice(0, 10)
}

/** Зона устройства — только для подписи «у вас сейчас 14:40», не для расчётов. */
export function viewerTimeZone(): string {
  return Intl.DateTimeFormat().resolvedOptions().timeZone
}
